import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  User, Flame, Zap, Star, Trophy, Calendar, Target, Award, BookOpen, GraduationCap, Check, X, ChevronRight
} from "lucide-react";
import axios from "axios";
import { API_BASE_URL } from "../lib/config";
import SettingsPanel from "../components/SettingsPanel";

const API_URL = API_BASE_URL;

const TESTS = [
  { 
    id: "staar", 
    name: "STAAR Test", 
    icon: "⭐",
    subjects: [
      { id: "staar-math", name: "Math" },
      { id: "staar-reading", name: "Reading" },
      { id: "staar-writing", name: "Writing" },
      { id: "staar-science", name: "Science" },
      { id: "staar-social", name: "Social Studies" },
    ]
  },
  { 
    id: "sat", 
    name: "SAT", 
    icon: "📚",
    subjects: [
      { id: "sat-math", name: "Math" },
      { id: "sat-reading", name: "Reading" },
      { id: "sat-writing", name: "Writing & Language" },
    ]
  },
  { 
    id: "act", 
    name: "ACT", 
    icon: "🎓",
    subjects: [
      { id: "act-english", name: "English" },
      { id: "act-math", name: "Math" },
      { id: "act-reading", name: "Reading" },
      { id: "act-science", name: "Science" },
      { id: "act-writing", name: "Writing (Optional)" },
    ]
  },
  { 
    id: "asvab", 
    name: "ASVAB", 
    icon: "🛡️",
    subjects: [
      { id: "asvab-arithmetic", name: "Arithmetic Reasoning" },
      { id: "asvab-word", name: "Word Knowledge" },
      { id: "asvab-paragraph", name: "Paragraph Comprehension" },
      { id: "asvab-math", name: "Math Knowledge" },
      { id: "asvab-science", name: "General Science" },
      { id: "asvab-auto", name: "Auto & Shop Information" },
      { id: "asvab-mechanical", name: "Mechanical Comprehension" },
      { id: "asvab-electronics", name: "Electronics Information" },
    ]
  },
  { 
    id: "citizenship", 
    name: "US Citizenship", 
    icon: "🇺🇸",
    subjects: [
      { id: "cit-civics", name: "Civics" },
      { id: "cit-history", name: "US History" },
      { id: "cit-government", name: "Government" },
      { id: "cit-reading", name: "Reading" },
      { id: "cit-writing", name: "Writing" },
    ]
  },
];

const Profile = () => {
  const { user, token, fetchUser } = useAuth();
  const [progress, setProgress] = useState(null);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(user?.grade || "");
  const [loading, setLoading] = useState(true);
  const [savingGrade, setSavingGrade] = useState(false);
  
  // Test & Subject state
  const [showTestPicker, setShowTestPicker] = useState(false);
  const [showSubjectPicker, setShowSubjectPicker] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [savingTest, setSavingTest] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.grade) {
      setSelectedGrade(user.grade);
    }
    if (user?.test) {
      const test = TESTS.find(t => t.id === user.test);
      setSelectedTest(test || null);
    }
    if (user?.topics) {
      const topicIds = user.topics.split(",");
      const subjects = [];
      TESTS.forEach(t => {
        t.subjects.forEach(s => {
          if (topicIds.includes(s.id)) {
            subjects.push(s);
          }
        });
      });
      setSelectedSubjects(subjects);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [progressData, gradesData] = await Promise.all([
        api.getProgress(),
        axios.get(`${API_URL}/grades`)
      ]);
      setProgress(progressData);
      setGrades(gradesData.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = async (e) => {
    const newGrade = e.target.value;
    setSelectedGrade(newGrade);
    setSavingGrade(true);
    try {
      await axios.put(
        `${API_URL}/user/grade`,
        { grade: newGrade },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUser();
      toast.success("Grade updated successfully!");
    } catch (error) {
      toast.error("Failed to update grade");
      setSelectedGrade(user?.grade || "");
    } finally {
      setSavingGrade(false);
    }
  };

  const handleTestSelect = (test) => {
    setSelectedTest(test);
    setSelectedSubjects([]);
    setShowTestPicker(false);
    setShowSubjectPicker(true);
  };

  const toggleSubject = (subject) => {
    setSelectedSubjects(prev => {
      if (prev.find(s => s.id === subject.id)) {
        return prev.filter(s => s.id !== subject.id);
      } else {
        return [...prev, subject];
      }
    });
  };

  const handleSubjectsDone = async () => {
    if (selectedSubjects.length === 0) {
      toast.error("Please select at least one subject");
      return;
    }
    
    setSavingTest(true);
    setShowSubjectPicker(false);
    
    try {
      const topics = selectedSubjects.map(s => s.id).join(",");
      await axios.put(
        `${API_URL}/user/test-subjects`,
        { test: selectedTest.id, topics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUser();
      toast.success("Test & subjects updated!");
    } catch (error) {
      toast.error("Failed to update test & subjects");
    } finally {
      setSavingTest(false);
    }
  };

  const getTestSummary = () => {
    if (!selectedTest) return "Not selected";
    if (selectedSubjects.length === 0) return selectedTest.name;
    return `${selectedTest.name}: ${selectedSubjects.map(s => s.name).join(", ")}`;
  };

  const achievements = [
    { id: "First 100 XP", icon: "⚡", name: "Power Up", desc: "Earn your first 100 XP" },
    { id: "Week Warrior", icon: "🔥", name: "Week Warrior", desc: "Maintain a 7-day streak" },
    { id: "Quiz Master", icon: "🎯", name: "Quiz Master", desc: "Answer 50 questions correctly" },
    { id: "Night Owl", icon: "🦉", name: "Night Owl", desc: "Study after midnight" },
    { id: "Early Bird", icon: "🌅", name: "Early Bird", desc: "Study before 6 AM" },
  ];

  const userAchievements = progress?.achievements || [];

  const getGradeName = (gradeId) => {
    const grade = grades.find(g => g.id === gradeId);
    return grade ? grade.name : gradeId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-bounce-subtle">
          <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">C</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="profile-page">
      {/* Profile Header */}
      <div className="card-pop text-center">
        <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <span className="text-4xl font-black text-white">
            {user?.name?.charAt(0).toUpperCase() || "?"}
          </span>
        </div>
        <h1 className="font-heading font-black text-2xl text-slate-800">{user?.name}</h1>
        <p className="text-slate-500">{user?.email}</p>
        
        {/* Level Badge */}
        <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-lime-100 rounded-full">
          <Star className="w-5 h-5 text-lime-600" />
          <span className="font-bold text-lime-700">Level {user?.level || 1}</span>
        </div>
      </div>

      {/* Grade Selection Card */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-indigo-500" />
          Your Grade Level
        </h2>
        
        <div className="flex items-center gap-4">
          <select
            value={selectedGrade}
            onChange={handleGradeChange}
            disabled={savingGrade}
            data-testid="grade-select-profile"
            className="flex-1 h-12 px-4 rounded-xl border-2 border-slate-200 bg-slate-50 font-medium text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 appearance-none cursor-pointer disabled:opacity-50"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '20px'
            }}
          >
            <option value="">Select your grade</option>
            <option value="6">6th Grade (Middle School)</option>
            <option value="7">7th Grade (Middle School)</option>
            <option value="8">8th Grade (Middle School)</option>
            <option value="9">9th Grade - Freshman (High School)</option>
            <option value="10">10th Grade - Sophomore (High School)</option>
            <option value="11">11th Grade - Junior (High School)</option>
            <option value="12">12th Grade - Senior (High School)</option>
            <option value="college">College</option>
            <option value="adult">Adult Learner</option>
          </select>
          
          {savingGrade && (
            <div className="w-8 h-8 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
          )}
          {!savingGrade && selectedGrade && (
            <div className="w-8 h-8 bg-lime-100 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-lime-600" />
            </div>
          )}
        </div>
        
        <p className="text-sm text-slate-400 mt-3">
          Your grade level helps us customize questions and content for you.
        </p>
      </div>

      {/* Test & Subject Selection Card */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-lime-500" />
          Your Test & Subjects
        </h2>
        
        <button
          type="button"
          onClick={() => setShowTestPicker(true)}
          disabled={savingTest}
          data-testid="test-subject-select"
          className="w-full min-h-12 px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 font-medium text-left flex items-center justify-between hover:border-indigo-300 transition-colors disabled:opacity-50"
        >
          <span className={selectedTest ? "text-slate-700" : "text-slate-400"}>
            {savingTest ? "Saving..." : getTestSummary()}
          </span>
          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
        </button>
        
        {selectedSubjects.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedSubjects.map(s => (
              <span key={s.id} className="px-3 py-1 bg-lime-100 text-lime-700 text-sm font-medium rounded-full">
                {s.name}
              </span>
            ))}
          </div>
        )}
        
        <p className="text-sm text-slate-400 mt-3">
          Choose which test and subjects you want to focus on.
        </p>
      </div>

      {/* Accessibility & Language Settings */}
      <SettingsPanel />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-pop text-center">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Zap className="w-6 h-6 text-indigo-500" />
          </div>
          <p className="font-heading font-black text-2xl text-slate-800">{progress?.total_xp || 0}</p>
          <p className="text-sm text-slate-500">Total XP</p>
        </div>

        <div className="card-pop text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Flame className="w-6 h-6 text-orange-500" />
          </div>
          <p className="font-heading font-black text-2xl text-slate-800">{progress?.streak || 0}</p>
          <p className="text-sm text-slate-500">Day Streak</p>
        </div>

        <div className="card-pop text-center">
          <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Star className="w-6 h-6 text-lime-500" />
          </div>
          <p className="font-heading font-black text-2xl text-slate-800">{progress?.level || 1}</p>
          <p className="text-sm text-slate-500">Level</p>
        </div>

        <div className="card-pop text-center">
          <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Trophy className="w-6 h-6 text-sky-500" />
          </div>
          <p className="font-heading font-black text-2xl text-slate-800">{userAchievements.length}</p>
          <p className="text-sm text-slate-500">Achievements</p>
        </div>
      </div>

      {/* Category Progress */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-500" />
          Test Progress
        </h2>
        
        <div className="space-y-4">
          {[
            { id: "sat", name: "SAT", color: "bg-indigo-500", lightColor: "bg-indigo-100" },
            { id: "act", name: "ACT", color: "bg-lime-500", lightColor: "bg-lime-100" },
            { id: "asvab", name: "ASVAB", color: "bg-orange-500", lightColor: "bg-orange-100" },
            { id: "staar", name: "STAAR", color: "bg-sky-500", lightColor: "bg-sky-100" },
            { id: "citizenship", name: "US Citizenship", color: "bg-red-500", lightColor: "bg-red-100" }
          ].map((test) => {
            const catProgress = progress?.categories_progress?.[test.id] || {};
            const accuracy = catProgress.total_questions > 0 
              ? Math.round((catProgress.correct_answers / catProgress.total_questions) * 100) 
              : 0;
            
            return (
              <div key={test.id} className="flex items-center gap-4">
                <div className={`w-10 h-10 ${test.lightColor} rounded-xl flex items-center justify-center`}>
                  <div className={`w-4 h-4 ${test.color} rounded-full`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-slate-700">{test.name}</span>
                    <span className="text-sm text-slate-500">
                      {catProgress.correct_answers || 0} / {catProgress.total_questions || 0} correct
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${test.color} rounded-full transition-all`}
                      style={{ width: `${accuracy}%` }}
                    />
                  </div>
                </div>
                <span className="font-bold text-slate-600 w-12 text-right">{accuracy}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Achievements */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-orange-500" />
          Achievements
        </h2>

        <div className="grid sm:grid-cols-2 gap-3">
          {achievements.map((achievement) => {
            const unlocked = userAchievements.includes(achievement.id);
            
            return (
              <div
                key={achievement.id}
                className={`p-4 rounded-2xl border-2 ${
                  unlocked 
                    ? "bg-lime-50 border-lime-200" 
                    : "bg-slate-50 border-slate-200 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                    unlocked ? "bg-lime-100" : "bg-slate-100 grayscale"
                  }`}>
                    {achievement.icon}
                  </div>
                  <div>
                    <p className={`font-bold ${unlocked ? "text-lime-700" : "text-slate-500"}`}>
                      {achievement.name}
                    </p>
                    <p className="text-sm text-slate-400">{achievement.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account Info */}
      <div className="card-pop">
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-slate-500" />
          Account Info
        </h2>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Grade Level</span>
            <span className="font-medium text-slate-700">{getGradeName(user?.grade) || "Not set"}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Current Test</span>
            <span className="font-medium text-slate-700">{selectedTest?.name || "Not set"}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Member Since</span>
            <span className="font-medium text-slate-700">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Last Activity</span>
            <span className="font-medium text-slate-700">
              {user?.last_activity ? new Date(user.last_activity).toLocaleDateString() : "Today"}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-slate-500">Account Type</span>
            <span className="px-3 py-1 bg-lime-100 text-lime-700 rounded-full text-sm font-bold">Free</span>
          </div>
        </div>
      </div>

      {/* Test Picker Modal */}
      {showTestPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTestPicker(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg text-slate-800">Select Your Test</h3>
              <button onClick={() => setShowTestPicker(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
              {TESTS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTestSelect(t)}
                  data-testid={`test-option-${t.id}`}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${
                    selectedTest?.id === t.id 
                      ? "border-lime-400 bg-lime-50" 
                      : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                  }`}
                >
                  <span className="text-3xl">{t.icon}</span>
                  <div className="text-left flex-1">
                    <p className="font-bold text-slate-700">{t.name}</p>
                    <p className="text-sm text-slate-500">{t.subjects.length} subjects</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Subject Picker Modal */}
      {showSubjectPicker && selectedTest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowSubjectPicker(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-800">{selectedTest.name}</h3>
                <p className="text-sm text-slate-500">Select subjects you need help with</p>
              </div>
              <button onClick={() => setShowSubjectPicker(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[50vh] p-4 space-y-2">
              {selectedTest.subjects.map((subject) => {
                const isSelected = selectedSubjects.find(s => s.id === subject.id);
                return (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => toggleSubject(subject)}
                    data-testid={`subject-option-${subject.id}`}
                    className={`w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? "bg-lime-50 border-lime-400" 
                        : "border-slate-200 hover:border-indigo-300"
                    }`}
                  >
                    <span className={`font-medium ${isSelected ? "text-lime-700" : "text-slate-700"}`}>
                      {subject.name}
                    </span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? "bg-lime-500 border-lime-500" : "border-slate-300"
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-slate-100 space-y-2">
              <p className="text-sm text-slate-500 text-center">
                {selectedSubjects.length} subject{selectedSubjects.length !== 1 ? "s" : ""} selected
              </p>
              <button 
                onClick={handleSubjectsDone} 
                className="w-full btn-primary"
                disabled={selectedSubjects.length === 0}
                data-testid="save-subjects-btn"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
